import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Volume2, VolumeX, Clock, Lightbulb, Eye } from 'lucide-react';
import { ZenerCard } from './ZenerCard';
import { getSymbolComponent } from './ZenerSymbols';
import { useAmbientSound } from '@/hooks/useAmbientSound';

const SYMBOLS = ['circle', 'cross', 'waves', 'square', 'star'];

// Lightweight Starfield - reduced stars for performance
const StarfieldBackground = React.memo(() => {
  const stars = useMemo(() => {
    const starArray = [];
    // Reduced from 100 to 30 stars for performance
    for (let i = 0; i < 30; i++) {
      starArray.push({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() > 0.8 ? 2 : 1,
        opacity: 0.3 + Math.random() * 0.7,
      });
    }
    return starArray;
  }, []);

  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
      <div className="absolute inset-0 bg-gradient-to-b from-[#030308] via-[#0a0a15] to-[#030308]" />
      {/* Static stars - no animation for performance */}
      {stars.map((star) => (
        <div
          key={star.id}
          className="absolute rounded-full bg-white"
          style={{
            left: `${star.x}%`,
            top: `${star.y}%`,
            width: star.size,
            height: star.size,
            opacity: star.opacity,
          }}
        />
      ))}
    </div>
  );
});

// Timer formatting helper
const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Durugörü Tips Component
const DurugoruTips = () => {
  const tips = [
    { id: 'tip-eye', icon: '👁️', text: 'Kaşlarınızın arasına, 3. göz bölgesine odaklanın' },
    { id: 'tip-breath', icon: '🌬️', text: 'Derin nefes alın, zihninizi boşaltın' },
    { id: 'tip-trust', icon: '✨', text: 'İlk içgüdüsel hisse güvenin' },
    { id: 'tip-feel', icon: '🔮', text: 'Düşünmeyin, hissedin' }
  ];
  
  return (
    <div className="glass-panel p-4 space-y-3 border-amber-500/20 bg-amber-500/5">
      <div className="flex items-center gap-2 text-amber-400">
        <Lightbulb className="w-4 h-4" />
        <span className="text-xs tracking-widest uppercase font-medium">3. Göz İpuçları</span>
      </div>
      <ul className="space-y-2">
        {tips.map((tip) => (
          <li key={tip.id} className="text-xs text-slate-400 flex items-start gap-2">
            <span>{tip.icon}</span>
            <span>{tip.text}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const TestScreen = ({ session, onGuess, lastResult, onEndSession }) => {
  const { isMuted, startSound, toggleMute } = useAmbientSound();
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [isRevealed, setIsRevealed] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  
  // Start sound when entering test screen
  useEffect(() => {
    startSound();
  }, [startSound]);
  
  // Timer effect
  useEffect(() => {
    const timer = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);
  
  useEffect(() => {
    if (lastResult) {
      setIsRevealed(true);
      setShowFeedback(true);
      
      const timer = setTimeout(() => {
        setIsRevealed(false);
        setShowFeedback(false);
        setSelectedSymbol(null);
      }, 2500);
      
      return () => clearTimeout(timer);
    }
  }, [lastResult]);
  
  const handleSymbolSelect = (symbol) => {
    if (isRevealed || !session) return;
    setSelectedSymbol(symbol);
    onGuess(symbol);
  };
  
  const handleEndSession = () => {
    if (window.confirm('Testi şimdi bitirmek istediğinizden emin misiniz? Mevcut ilerlemeniz kaydedilecek.')) {
      onEndSession();
    }
  };
  
  const accuracy = session?.total > 0 ? ((session.hits / session.total) * 100).toFixed(1) : '0.0';
  const deckSize = session?.deckSequence?.length || 25;
  
  return (
    <div className="min-h-screen flex items-center justify-center p-4 md:p-8 relative" data-testid="test-screen">
      {/* Starfield Background */}
      <StarfieldBackground />
      
      {/* Sound Toggle - Fixed position */}
      <button
        onClick={toggleMute}
        className="fixed top-6 left-6 z-50 flex items-center gap-2 glass-panel px-3 py-2 text-sm text-slate-300 hover:text-white transition-all hover:border-violet-500/50"
        data-testid="test-sound-toggle"
        title={isMuted ? 'Ambient Ses Aç' : 'Ambient Ses Kapat'}
      >
        {isMuted ? (
          <VolumeX className="w-4 h-4" />
        ) : (
          <Volume2 className="w-4 h-4 text-violet-400" />
        )}
      </button>
      
      {/* Timer - Fixed position */}
      <div 
        className="fixed top-6 left-20 z-50 flex items-center gap-1.5 glass-panel px-3 py-2"
        data-testid="session-timer"
      >
        <Clock className="w-3 h-3 text-cyan-400/70" />
        <span className="text-xs font-mono text-slate-400">{formatTime(elapsedTime)}</span>
      </div>
      
      <div className="w-full max-w-6xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Main test area */}
          <div className="lg:col-span-8 space-y-8">
            {/* Progress indicator */}
            <div className="text-center space-y-2">
              <p className="text-xs tracking-widest uppercase text-slate-600">
                Tur {session?.currentIndex + 1} / {deckSize}
              </p>
              <div className="w-full h-1 bg-white/5 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-violet-500 to-cyan-500"
                  initial={{ width: 0 }}
                  animate={{ width: `${((session?.currentIndex || 0) / deckSize) * 100}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
              
              {/* End Test Button */}
              <button
                onClick={handleEndSession}
                className="text-xs text-slate-600 hover:text-slate-400 transition-colors mt-2"
                data-testid="end-session-btn"
              >
                Testi Bitir
              </button>
            </div>
            
            {/* Card display */}
            <div className="flex justify-center py-8">
              <ZenerCard
                symbol={lastResult?.actualSymbol || 'circle'}
                isRevealed={isRevealed}
              />
            </div>
            
            {/* Feedback */}
            <AnimatePresence>
              {showFeedback && lastResult && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="text-center"
                  data-testid="feedback-message"
                >
                  <p className={`text-2xl font-heading ${lastResult.isMatch ? 'text-green-400' : 'text-slate-500'}`}>
                    {lastResult.isMatch ? '✨ Bildin!' : 'Eşleşmedi'}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
            
            {/* Symbol selection */}
            <div className="flex justify-center">
              <div className="flex gap-4 flex-wrap justify-center max-w-xl">
                {SYMBOLS.map((symbol) => {
                  const SymbolComponent = getSymbolComponent(symbol);
                  return (
                    <button
                      key={symbol}
                      onClick={() => handleSymbolSelect(symbol)}
                      disabled={isRevealed}
                      className="h-24 w-24 rounded-xl border border-white/5 bg-black/40 backdrop-blur-md transition-all hover:border-violet-500/50 hover:bg-violet-500/10 hover:shadow-[0_0_15px_rgba(139,92,246,0.2)] active:scale-95 flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed text-slate-400 hover:text-violet-400"
                      data-testid={`symbol-btn-${symbol}`}
                    >
                      <div className="w-12 h-12">
                        <SymbolComponent />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          
          {/* Stats panel */}
          <div className="lg:col-span-4 space-y-4">
            <div className="glass-panel p-6 space-y-6">
              <h3 className="text-xs tracking-widest uppercase text-violet-400 font-medium">
                Test İstatistikleri
              </h3>
              
              <div className="space-y-4">
                <div>
                  <p className="text-slate-500 text-xs mb-1">Doğruluk</p>
                  <p className="text-3xl font-heading text-slate-100" data-testid="accuracy-display">
                    {accuracy}%
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-slate-500 text-xs mb-1">İsabet</p>
                    <p className="text-xl font-heading text-green-400" data-testid="hits-display">
                      {session?.hits || 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs mb-1">Kaçan</p>
                    <p className="text-xl font-heading text-slate-500" data-testid="misses-display">
                      {session?.misses || 0}
                    </p>
                  </div>
                </div>
                
                <div>
                  <p className="text-slate-500 text-xs mb-1">Mevcut Seri</p>
                  <p className="text-xl font-heading text-cyan-400" data-testid="current-streak-display">
                    {session?.currentStreak || 0}
                  </p>
                </div>
                
                <div>
                  <p className="text-slate-500 text-xs mb-1">En İyi Seri</p>
                  <p className="text-xl font-heading text-violet-400" data-testid="best-streak-display">
                    {session?.bestStreak || 0}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Durugörü Tips */}
            <DurugoruTips />
            
            {/* Transparency info */}
            <div className="glass-panel p-4 space-y-2">
              <div className="flex items-center gap-2 text-violet-400/70">
                <Eye className="w-3 h-3" />
                <span className="text-xs tracking-widest uppercase font-medium">Şeffaflık</span>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed">
                Tüm kartlar testin başında oluşturulur.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
